/**
 * El avatar del jugador: lo que elige, y como se guarda.
 *
 * ---------------------------------------------------------------------------
 * QUE ES UN AVATAR AQUI
 * ---------------------------------------------------------------------------
 * **Cinco numeros.** No un fichero, no una imagen: cinco indices dentro del
 * catalogo de `shared/avatar-catalogo.json`.
 *
 *   cuerpo  0..1   masculino / femenino
 *   peinado 0..5   la forma del pelo (se construye, no se descarga)
 *   pelo    0..5   el color del pelo
 *   piel    0..5   el tono de piel
 *   traje   0..6   la ropa, que se PINTA sobre la textura del cuerpo
 *
 * Por que importa que sean numeros: viajan en el perfil, asi que el avatar
 * sigue al jugador de un movil a otro, y **no cuestan un solo byte de
 * descarga**. Un creador donde cada combinacion fuera un fichero seria
 * imposible en un juego que tiene 700 KB de presupuesto entero.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE FICHERO NO DIBUJA NADA
 * ---------------------------------------------------------------------------
 * Aqui solo hay catalogo y reglas: que existe, que vale y que esta
 * desbloqueado. Lo de pintar esta en `ropa.ts` y `peinado.ts`, y la pantalla
 * en `main.ts`.
 *
 * Asi este fichero entra en la primera descarga —la pantalla del avatar lo
 * necesita— sin arrastrar el sistema de animacion de three detras.
 */

import catalogo from '@compartido/avatar-catalogo.json';

export interface Cuerpo {
  id: string;
  modelo: string;
}

export interface Peinado {
  id: string;
  forma: string;
  volumen: number;
  largo: number;
}

export interface Pelo {
  id: string;
  color: string;
}

export interface Piel {
  id: string;
  muestra: string;
  tinte: string;
  modo: 'multiply' | 'screen';
  alfa: number;
}

export interface Traje {
  id: string;
  camiseta: string;
  pantalon: string;
  zapatillas: string;
  /** `null` = lo tiene todo el mundo. Si no, el nombre del hito que lo abre. */
  requisito: string | null;
}

/** Lo que el jugador eligio. Cinco indices, nada mas. */
export interface Avatar {
  cuerpo: number;
  peinado: number;
  pelo: number;
  piel: number;
  traje: number;
}

export const CUERPOS = catalogo.cuerpos as Cuerpo[];
export const PEINADOS = catalogo.peinados as Peinado[];
export const PELOS = catalogo.pelos as Pelo[];
export const PIELES = catalogo.pieles as Piel[];
export const TRAJES = catalogo.trajes as Traje[];
export const AVATAR_POR_DEFECTO: Avatar = catalogo.porDefecto as Avatar;

/**
 * Deja un indice dentro de su lista.
 *
 * **Recorta en vez de fallar, a proposito.** Esto se llama con lo que venga
 * del servidor, de `localStorage` o de una version vieja del catalogo. Si un
 * dia se quita un traje, el jugador que lo tuviera puesto se encuentra con
 * otro, no con un juego que no arranca.
 */
function dentro(valor: unknown, largo: number, pordefecto: number): number {
  const n = Math.trunc(Number(valor));
  if (!Number.isFinite(n) || n < 0 || n >= largo) return pordefecto;
  return n;
}

/**
 * Convierte cualquier cosa en un avatar valido.
 *
 * `desbloqueados` son los requisitos que este jugador ha cumplido, tal y como
 * los manda el servidor. Un traje que pide un requisito que no esta en esa
 * lista **se cambia por el de por defecto**: asi, si alguien pierde el Pase de
 * Temporada, no se queda con un juego que el servidor rechaza.
 */
export function normalizar(crudo: unknown, desbloqueados: string[] = []): Avatar {
  const o = (crudo ?? {}) as Record<string, unknown>;
  const d = AVATAR_POR_DEFECTO;
  const avatar: Avatar = {
    cuerpo: dentro(o.cuerpo, CUERPOS.length, d.cuerpo),
    peinado: dentro(o.peinado, PEINADOS.length, d.peinado),
    pelo: dentro(o.pelo, PELOS.length, d.pelo),
    piel: dentro(o.piel, PIELES.length, d.piel),
    traje: dentro(o.traje, TRAJES.length, d.traje),
  };
  if (!trajeDisponible(avatar.traje, desbloqueados)) avatar.traje = d.traje;
  return avatar;
}

/** Si este jugador puede ponerse ese traje. */
export function trajeDisponible(indice: number, desbloqueados: string[]): boolean {
  const traje = TRAJES[indice];
  if (!traje) return false;
  if (traje.requisito === null) return true;
  return desbloqueados.includes(traje.requisito);
}

/** Dos avatares son el mismo si sus cinco numeros lo son. */
export function iguales(a: Avatar, b: Avatar): boolean {
  return (
    a.cuerpo === b.cuerpo &&
    a.peinado === b.peinado &&
    a.pelo === b.pelo &&
    a.piel === b.piel &&
    a.traje === b.traje
  );
}

/** Los colores de la ropa que toca pintar, a partir del traje elegido. */
export function atuendoDe(avatar: Avatar): {
  camiseta: string;
  pantalon: string;
  zapatillas: string;
  piel: Piel;
  pelo: string;
} {
  const traje = TRAJES[avatar.traje] ?? TRAJES[0]!;
  const piel = PIELES[avatar.piel] ?? PIELES[0]!;
  const pelo = PELOS[avatar.pelo] ?? PELOS[0]!;
  return {
    camiseta: traje.camiseta,
    pantalon: traje.pantalon,
    zapatillas: traje.zapatillas,
    piel,
    pelo: pelo.color,
  };
}

/** La ruta del modelo del cuerpo elegido. */
export function modeloDe(avatar: Avatar): string {
  return (CUERPOS[avatar.cuerpo] ?? CUERPOS[0]!).modelo;
}
